import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiUpload,
  FiFile,
  FiFileText,
  FiTrash2,
  FiBook,
  FiZap,
  FiClipboard,
  FiLayers,
  FiX,
  FiLoader,
  FiCheck,
  FiAlertCircle,
  FiMessageSquare
} from 'react-icons/fi';
import DocumentChatV2 from './DocumentChatV2';
import {
  getAllFiles,
  saveFileToStorage,
  deleteFileFromStorage,
  formatFileSize,
  base64ToFile
} from '../utils/fileStorage';
import styles from '../styles/documentLibrary.module.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const DocumentLibrary = ({ onClose, onGenerateFromDoc }) => {
  const [storageWarning, setStorageWarning] = useState(false);
  const [storageUsage, setStorageUsage] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingType, setProcessingType] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pageRange, setPageRange] = useState({ start: 1, end: null }); // null = process all pages
  const [totalPages, setTotalPages] = useState(null);
  const [showPageSelector, setShowPageSelector] = useState(false);
  const [processedDocCache, setProcessedDocCache] = useState({});
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [showDocumentChat, setShowDocumentChat] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [initialPageNumber, setInitialPageNumber] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadDocuments();
    checkStorage();
  }, []);

  // Listen for request to open document at specific page
  useEffect(() => {
    const handleOpenDocumentAtPage = (event) => {
      const { document, pageNumber } = event.detail;
      console.log("Opening document at page:", document, pageNumber);
      
      // Find the document in our list
      const doc = documents.find(d => d.name === document.name);
      if (doc) {
        setSelectedDoc(doc);
        setShowDocumentChat([doc]);
        setInitialPageNumber(pageNumber);
      }
    };

    window.addEventListener("openDocumentInLibrary", handleOpenDocumentAtPage);
    return () => {
      window.removeEventListener("openDocumentInLibrary", handleOpenDocumentAtPage);
    };
  }, [documents]);

  const checkStorage = () => {
    try {
      const storageStr = JSON.stringify(localStorage);
      const usedMB = storageStr.length / (1024 * 1024);
      const limitMB = 10; // Most browsers ~10MB localStorage limit
      const usagePercent = (usedMB / limitMB) * 100;
      
      setStorageUsage(usagePercent);
      if (usagePercent > 80) {
        setStorageWarning(true);
        console.warn(`⚠️ localStorage is ${usagePercent.toFixed(0)}% full (${usedMB.toFixed(2)}MB / ~${limitMB}MB)`);
      }
    } catch (err) {
      console.error('Error checking storage:', err);
    }
  };

  const handleClearStorage = () => {
    if (window.confirm('This will delete ALL uploaded documents. Are you sure?')) {
      localStorage.removeItem('quizmate_files');
      sessionStorage.removeItem('quizmate_session_files');
      loadDocuments();
      checkStorage();
      setSuccess('Storage cleared successfully');
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const loadDocuments = () => {
    const files = getAllFiles();
    setDocuments(files);
  };

  const toggleDocumentSelection = (doc) => {
    setSelectedDocuments(prev => {
      const isSelected = prev.some(d => d.id === doc.id);
      if (isSelected) {
        return prev.filter(d => d.id !== doc.id);
      } else {
        return [...prev, doc];
      }
    });
  };

  const handleStartChat = async () => {
    // If chat is minimized, just un-minimize it
    if (isChatMinimized && showDocumentChat) {
      setIsChatMinimized(false);
      return;
    }

    // Ensure selectedDoc is in selectedDocuments if it's set
    let docsToChat = [...selectedDocuments];
    if (selectedDoc && !docsToChat.some(d => d.id === selectedDoc.id)) {
      docsToChat = [selectedDoc, ...docsToChat];
      setSelectedDocuments(docsToChat);
    }
    
    if (docsToChat.length === 0) {
      setError('Please select at least one document to chat with');
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    setChatLoading(true);
    
    try {
      // Load document content for selected documents
      const documentsWithContent = await Promise.all(
      docsToChat.map(async (doc) => {
        // Check if document is already processed in cache
        if (processedDocCache[doc.id]) {
          const cached = processedDocCache[doc.id];
          let content = '';
          
          if (cached.chunks && Array.isArray(cached.chunks)) {
            content = cached.chunks
              .map(chunk => chunk.analysis || chunk.text || '')
              .filter(text => text && text !== 'Parse failure')
              .join('\n\n');
          } else if (cached.text) {
            content = cached.text;
          }

          return {
            ...doc,
            content,
            pageRange: `1-${cached.chunks?.length || 'all'}`
          };
        }

        // If not cached, try to get from file content
        const file = base64ToFile(doc.content, doc.name, doc.type);
        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await fetch(`${API_BASE}/api/process_document`, {
            method: 'POST',
            body: formData,
          });

          if (!response.ok) throw new Error('Failed to process document');

          const data = await response.json();
          
          // Cache it
          setProcessedDocCache(prev => ({
            ...prev,
            [doc.id]: data
          }));

          let content = '';
          if (data.chunks && Array.isArray(data.chunks)) {
            content = data.chunks
              .map(chunk => chunk.analysis || chunk.text || '')
              .filter(text => text && text !== 'Parse failure')
              .join('\n\n');
          } else if (data.text) {
            content = data.text;
          }

          return {
            ...doc,
            content,
            pageRange: `1-${data.chunks?.length || 'all'}`
          };
        } catch (err) {
          console.error('Error processing document:', err);
          return {
            ...doc,
            content: '',
            pageRange: ''
          };
        }
        })
      );

      // Open chat with loaded documents
      setShowDocumentChat(documentsWithContent);
    } catch (err) {
      console.error('Error loading documents for chat:', err);
      setError('Failed to load documents. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setChatLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    console.log('📁 Files selected:', files.length, files.map(f => f.name));
    if (files.length === 0) return;

    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      for (const file of files) {
        // Validate file type
        const validTypes = [
          'application/pdf',
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'text/plain',
          'text/markdown'
        ];
        
        if (!validTypes.includes(file.type) && 
            !file.name.endsWith('.pdf') && 
            !file.name.endsWith('.doc') && 
            !file.name.endsWith('.docx') &&
            !file.name.endsWith('.txt') &&
            !file.name.endsWith('.md')) {
          setError(`Unsupported file type: ${file.name}. Supported: PDF, DOC, DOCX, TXT, MD`);
          continue;
        }

        // Validate file size (max 50MB for textbooks)
        if (file.size > 50 * 1024 * 1024) {
          setError(`File too large: ${file.name}. Maximum size: 50MB`);
          continue;
        }
        
        // Warn about large files
        if (file.size > 10 * 1024 * 1024) {
          console.log(`Large file detected (${(file.size / (1024 * 1024)).toFixed(2)}MB): ${file.name}. Using smart chunking.`);
        }

        console.log('💾 Saving file to storage:', file.name);
        await saveFileToStorage(file);
        console.log('✅ File saved successfully:', file.name);
      }

      console.log('📚 Reloading documents...');
      loadDocuments();
      checkStorage(); // Update storage usage
      const updatedDocs = getAllFiles();
      console.log('📚 Documents after upload:', updatedDocs.length, updatedDocs.map(d => d.name));
      setSuccess(`Successfully uploaded ${files.length} file(s)`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('❌ Upload error:', err);
      setError(err.message || 'Failed to upload files');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      deleteFileFromStorage(docId);
      loadDocuments();
      if (selectedDoc?.id === docId) {
        setSelectedDoc(null);
      }
      setSuccess('Document deleted successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to delete document');
    }
  };

  // When document is selected, analyze it for page count
  useEffect(() => {
    if (selectedDoc) {
      // Estimate pages from file size and type
      const estimatedPages = Math.ceil(selectedDoc.size / (1024 * 50)); // ~50KB per page estimate
      setTotalPages(estimatedPages);
      // Default to ALL pages instead of just first 10
      setPageRange({ start: 1, end: estimatedPages });
      setShowPageSelector(true);
    } else {
      setShowPageSelector(false);
      setTotalPages(null);
    }
  }, [selectedDoc]);

  const handleGenerate = async (type) => {
    if (!selectedDoc) return;

    setProcessing(true);
    setProcessingType(type);
    setError(null);

    try {
      let responseData;
      
      // Check cache first to avoid rate limits
      // DISABLE CACHE TO FIX CONTEXT LEAKS
      // if (processedDocCache[selectedDoc.id]) {
      //   console.log('💾 Using cached document data for:', selectedDoc.name);
      //   responseData = processedDocCache[selectedDoc.id];
      // } else {
        console.log('🔄 Processing document for first time:', selectedDoc.name);
        
        // Convert base64 back to file
        const file = base64ToFile(selectedDoc.content, selectedDoc.name, selectedDoc.type);
        
        const formData = new FormData();
        formData.append('file', file);

        // Upload and process document
        const uploadResponse = await fetch(`${API_BASE}/api/process_document`, {
          method: 'POST',
          body: formData,
        });

        if (!uploadResponse.ok) {
          let errorMessage = 'Failed to process document';
          
          try {
            const errorData = await uploadResponse.json();
            errorMessage = errorData.error || errorMessage;
            console.error('❌ Processing error:', errorData);
          } catch (parseError) {
            const errorText = await uploadResponse.text();
            console.error('❌ Upload response error:', errorText);
            errorMessage = errorText || errorMessage;
          }
          
          // Check if it's a rate limit error
          if (uploadResponse.status === 429) {
            throw new Error('⏱️ Rate limit exceeded. Please wait before processing more documents (15 per hour limit).');
          }
          
          throw new Error(errorMessage);
        }

        responseData = await uploadResponse.json();
        console.log('📄 Document processing response:', responseData);
        
        // Validate response structure
        if (!responseData) {
          console.error('❌ Response is null or undefined');
          throw new Error('No response from server.');
        }
        
        // Check for error in response
        if (responseData.error) {
          console.error('❌ Server returned error:', responseData.error);
          throw new Error(responseData.error);
        }
        
        // Handle large document warnings and suggestions
        if (responseData.warning) {
          console.warn('⚠️ Large document warning:', responseData.warning);
          // Show warning in alert with clear formatting
          const warningMsg = responseData.warning;
          alert(warningMsg);
        }
        
        // Apply suggested range for large documents
        if (responseData.suggested_range) {
          console.log('💡 Applying suggested range:', responseData.suggested_range);
          setPageRange(responseData.suggested_range);
        }
        
        // Check for expected data structure
        if (!responseData.pages && !responseData.text && !responseData.chunks) {
          console.error('❌ Invalid response structure. Received keys:', Object.keys(responseData));
          console.error('Full response:', JSON.stringify(responseData, null, 2));
          throw new Error(`Invalid response from server. Expected 'pages', 'text', or 'chunks' but got: ${Object.keys(responseData).join(', ')}`);
        }
        
        // Warn if using old format
        if (responseData.chunks && !responseData.pages) {
          console.warn('⚠️⚠️⚠️ OLD BACKEND FORMAT DETECTED! ⚠️⚠️⚠️');
          console.warn('Please RESTART the backend server to use the new format.');
          console.warn('Old format may not extract text correctly.');
        }
        
        // Cache the processed document
        // DISABLE CACHE SAVING
        // setProcessedDocCache(prev => ({
        //   ...prev,
        //   [selectedDoc.id]: responseData
        // }));
        console.log('💾 (Skipped) Cached document data for:', selectedDoc.name);
      // }
      
      let processedText = '';
      
      // Handle NEW format: {pages: [{page_number, text}], text: "full text"}
      if (responseData.pages && Array.isArray(responseData.pages)) {
        console.log('📄 NEW FORMAT: Document has pages, total:', responseData.pages.length);
        
        // Update total pages and page range FIRST
        const actualTotalPages = responseData.pages.length;
        if (actualTotalPages !== totalPages) {
          setTotalPages(actualTotalPages);
        }
        
        // Calculate correct end page
        let effectiveEndPage = pageRange.end;
        
        // Auto-expand to all pages in these cases:
        // 1. pageRange.end is null (not set yet)
        // 2. pageRange.end matches the old estimated totalPages (user didn't customize)
        // 3. pageRange.end is beyond actual pages (invalid range)
        const shouldAutoExpand = (
          effectiveEndPage === null || 
          effectiveEndPage === totalPages || 
          effectiveEndPage > actualTotalPages
        );
        
        if (shouldAutoExpand) {
          console.log(`🔄 Auto-expanding page range from ${pageRange.end} to ${actualTotalPages} (all pages)`);
          effectiveEndPage = actualTotalPages;
          setPageRange({ start: 1, end: actualTotalPages });
        } else {
          console.log(`✓ Using user-selected page range: ${pageRange.start}-${effectiveEndPage} of ${actualTotalPages}`);
        }
        
        // Filter pages by selected range
        const selectedPages = responseData.pages.filter(p => 
          p.page_number >= pageRange.start && p.page_number <= effectiveEndPage
        );
        
        console.log(`📑 Selected ${selectedPages.length} pages from range ${pageRange.start}-${effectiveEndPage}`);
        
        // Extract text from each page
        processedText = selectedPages
          .map(page => {
            const text = page.text || '';
            if (!text.trim()) {
              console.warn(`⚠️ Page ${page.page_number} has no text`);
            }
            return text;
          })
          .filter(text => text && text.trim().length > 0)
          .join('\n\n');
        
        console.log(`✅ Extracted text from ${selectedPages.length} pages, total length: ${processedText.length} chars`);
      }
      // Handle OLD format: {chunks: [{page_number, text}]}
      else if (responseData.chunks && Array.isArray(responseData.chunks)) {
        console.warn('⚠️ OLD FORMAT DETECTED: Using chunks (please restart backend)');
        
        // Filter chunks by page range (null end = all chunks)
        const maxPage = Math.max(...responseData.chunks.map(c => c.page_number || 1));
        const endPage = pageRange.end === null ? maxPage : pageRange.end;
        const selectedChunks = responseData.chunks.filter(chunk => 
          chunk.page_number >= pageRange.start && chunk.page_number <= endPage
        );
        
        console.log(`📑 Selected ${selectedChunks.length} chunks from range ${pageRange.start}-${endPage}`);
        
        // Extract text from chunks
        processedText = selectedChunks
          .map(chunk => chunk.text || '')
          .filter(text => text && text.trim().length > 0)
          .join('\n\n');
        
        // Update total pages based on chunks
        if (maxPage !== totalPages) {
          setTotalPages(maxPage);
        }
        
        console.log(`✅ Extracted text from ${selectedChunks.length} chunks, total length: ${processedText.length} chars`);
      }
      // Fallback to full text if pages not available
      else if (responseData.text) {
        console.log('📄 Using full text format');
        const text = responseData.text;
        
        // Estimate page boundaries based on character count
        const estimatedCharsPerPage = 2000;
        const startChar = (pageRange.start - 1) * estimatedCharsPerPage;
        const endChar = pageRange.end * estimatedCharsPerPage;
        processedText = text.substring(startChar, Math.min(endChar, text.length));
        
        console.log(`✅ Extracted text substring, length: ${processedText.length} chars`);
      } else {
        console.error('❌ No valid data format received:', Object.keys(responseData));
        console.error('Expected: pages, chunks, or text');
        console.error('⚠️ If you see "chunks" or "aggregated_result", RESTART THE BACKEND!');
      }
      
      // Check if we got any text
      if (!processedText || processedText.trim().length === 0) {
        throw new Error('No text content extracted from document');
      }
      
      console.log(`✅ Extracted ${processedText.length} characters of text`);
      
      // Inform user about processing with specific messages for notes
      if (type === 'notes') {
        // Calculate batches for page-by-page processing
        const usePageByPage = responseData.pages && responseData.pages.length > 10;
        
        if (usePageByPage) {
          const pagesPerBatch = 5;
          const totalPages = responseData.pages.length;
          const numBatches = Math.ceil(totalPages / pagesPerBatch);
          const estimatedMinutes = Math.ceil((numBatches * 30 + 60) / 60); // 30s per batch + 60s combination
          
          console.log(`📚 Large document detected - will process ${totalPages} pages in ${numBatches} detailed batches`);
          setSuccess(`🔍 DETAILED ANALYSIS MODE: Processing ${totalPages} pages in ${numBatches} batches. Extracting ALL definitions, examples, citations, and code. This will take approximately ${estimatedMinutes}-${estimatedMinutes + 2} minutes for complete coverage. Please be patient...`);
        } else if (processedText.length > 15000) {
          setSuccess(`📖 Processing ${Math.round(processedText.length/1000)}K chars... Generating comprehensive study notes (30-45 seconds)...`);
        } else {
          setSuccess(`📝 Analyzing document... Creating detailed study notes (15-25 seconds)...`);
        }
      } else if (processedText.length > 30000) {
        console.log('📚 Large document detected - using intelligent processing');
        setSuccess(`Processing large document (${Math.round(processedText.length/1000)}K chars). This may take a moment...`);
      }
      
      // Add page range note
      const endPage = pageRange.end === null ? totalPages : pageRange.end;
      const rangeNote = (pageRange.start === 1 && endPage === totalPages) 
        ? `\n\n[Complete document - All ${totalPages} pages]`
        : `\n\n[Content from pages ${pageRange.start}-${endPage} of ${totalPages}]`;
      processedText += rangeNote;

      // Generate content based on type
      let endpoint = '';
      let payload = {};

      if (type === 'quiz') {
        endpoint = '/api/generate';
        payload = {
          type: 'quiz',
          text: processedText
        };
      } else if (type === 'test') {
        // Use generate_test endpoint with extracted text
        endpoint = '/api/generate_test';
        payload = {
          topic: selectedDoc.name.replace(/\.[^/.]+$/, ''), // Remove file extension
          question_count: 15,
          test_type: 'mixed',
          difficulty: 'medium',
          time_limit: 20,
          show_feedback: true,
          document_text: processedText // Pass processed document text
        };
      } else if (type === 'flashcards') {
        endpoint = '/api/generate';
        payload = {
          type: 'flashcards',
          text: processedText
        };
      } else if (type === 'quotes') {
        endpoint = '/api/generate';
        payload = {
          type: 'quotes',
          text: processedText
        };
      } else if (type === 'notes') {
        // For comprehensive processing, use page-by-page or small chunk processing
        const usePageByPage = responseData.pages && responseData.pages.length > 0;
        
        if (usePageByPage && responseData.pages.length > 10) {
          console.log(`📚 Large document (${responseData.pages.length} pages) - using detailed page-by-page processing`);
          
          // Group pages into small batches (5-8 pages per batch for detail)
          const pagesPerBatch = 5;
          const batches = [];
          const actualPages = responseData.pages.filter(p => 
            p.page_number >= pageRange.start && 
            p.page_number <= (pageRange.end || responseData.pages.length)
          );
          
          for (let i = 0; i < actualPages.length; i += pagesPerBatch) {
            batches.push(actualPages.slice(i, i + pagesPerBatch));
          }
          
          console.log(`📦 Split into ${batches.length} batches (${pagesPerBatch} pages each)`);
          
          // Process each batch with detailed extraction
          const batchResults = [];
          let totalProcessed = 0;
          
          for (let i = 0; i < batches.length; i++) {
            const batchNum = i + 1;
            const batch = batches[i];
            const batchText = batch.map(p => p.text).join('\n\n--- PAGE BREAK ---\n\n');
            const pageNumbers = batch.map(p => p.page_number);
            
            totalProcessed += batch.length;
            const percentComplete = Math.round((totalProcessed / actualPages.length) * 100);
            
            console.log(`📤 Processing batch ${batchNum}/${batches.length} (Pages ${pageNumbers[0]}-${pageNumbers[pageNumbers.length-1]})`);
            setSuccess(`🔍 Analyzing pages ${pageNumbers[0]}-${pageNumbers[pageNumbers.length-1]} (${percentComplete}% complete)... Extracting definitions, examples, citations...`);
            
            try {
              const batchResponse = await fetch(`${API_BASE}/api/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  type: 'notes_detailed_batch',
                  text: batchText,
                  batch_number: batchNum,
                  total_batches: batches.length,
                  page_numbers: pageNumbers,
                  extract_all: true  // Flag for maximum detail
                })
              });
              
              if (!batchResponse.ok) {
                throw new Error(`Batch ${batchNum} failed`);
              }
              
              const batchData = await batchResponse.json();
              batchResults.push({
                pages: pageNumbers,
                content: batchData.result
              });
              console.log(`✅ Batch ${batchNum}/${batches.length} completed (Pages ${pageNumbers[0]}-${pageNumbers[pageNumbers.length-1]})`);
            } catch (err) {
              console.error(`❌ Batch ${batchNum} failed:`, err);
              throw new Error(`Failed to process pages ${pageNumbers[0]}-${pageNumbers[pageNumbers.length-1]}`);
            }
          }
          
          // Combine all batches with validation
          console.log('🔗 Integrating all sections and validating completeness...');
          setSuccess('🔗 Integrating all sections, organizing structure, validating completeness...');
          
          const combinedResponse = await fetch(`${API_BASE}/api/generate`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'notes_final_integration',
              batches: batchResults,
              total_pages: actualPages.length,
              document_name: selectedDoc.name
            })
          });
          
          if (!combinedResponse.ok) {
            throw new Error('Failed to integrate sections');
          }
          
          const finalData = await combinedResponse.json();
          console.log('✅ All sections integrated and validated');
          
          // Send to parent
          onGenerateFromDoc(type, finalData, selectedDoc.name);
          setSuccess(`✅ Comprehensive study notes generated! Thoroughly analyzed all ${actualPages.length} pages in ${batches.length} detailed batches.`);
          return;
        }
        
        // For smaller documents, process normally
        endpoint = '/api/generate';
        payload = {
          type: 'notes',
          text: processedText
        };
        console.log(`📤 Sending to API - Type: ${type}, Text length: ${processedText.length}, Preview: ${processedText.substring(0, 100)}...`);
      }

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate ${type}`);
      }

      const data = await response.json();
      
      // Notify parent component with generated content
      if (onGenerateFromDoc) {
        onGenerateFromDoc(type, data, selectedDoc.name);
      }

      setSuccess(`Successfully generated ${type} from ${selectedDoc.name}`);
      setTimeout(() => {
        setSuccess(null);
        if (onClose) onClose();
      }, 2000);

    } catch (err) {
      console.error(`❌ Generation error (${type}):`, err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        selectedDoc: selectedDoc?.name
      });
      setError(err.message || `Failed to generate ${type}`);
    } finally {
      setProcessing(false);
      setProcessingType(null);
    }
  };

  const getFileIcon = (type, name) => {
    if (type?.includes('pdf') || name?.endsWith('.pdf')) {
      return <FiFile size={20} color="#ef4444" />;
    }
    if (type?.includes('word') || type?.includes('document') || name?.endsWith('.doc') || name?.endsWith('.docx')) {
      return <FiFileText size={20} color="#3b82f6" />;
    }
    return <FiFileText size={20} color="#6b7280" />;
  };

  return (
    <div className={styles.overlay}>
      <motion.div
        className={styles.container}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
      >
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>Document Library</h2>
            <p className={styles.subtitle}>Upload and generate content from your documents</p>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        {/* Messages */}
        <AnimatePresence>
          {error && (
            <motion.div
              key="error-message"
              className={styles.errorMessage}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <FiAlertCircle size={18} />
              {error}
            </motion.div>
          )}
          {success && (
            <motion.div
              key="success-message"
              className={styles.successMessage}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
            >
              <FiCheck size={18} />
              {success}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Storage Warning */}
        {storageWarning && (
          <div className={styles.storageWarning}>
            <FiAlertCircle size={18} />
            <div className={styles.storageWarningText}>
              <strong>Storage Almost Full!</strong>
              <p>Your browser storage is {storageUsage.toFixed(0)}% full. Delete old documents or clear storage.</p>
            </div>
            <button className={styles.clearStorageBtn} onClick={handleClearStorage}>
              Clear All Storage
            </button>
          </div>
        )}

        <div className={styles.content}>
          {/* Upload Section */}
          <div className={styles.uploadSection}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.txt,.md"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <button
              className={styles.uploadBtn}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <>
                  <FiLoader size={20} className={styles.spinner} />
                  Uploading... Please wait
                </>
              ) : (
                <>
                  <FiUpload size={20} />
                  Upload Documents
                </>
              )}
            </button>
            <p className={styles.uploadHint}>
              Supported: PDF, DOC, DOCX, TXT, MD (Max 50MB) • Automatic page splitting • Process any number of pages
            </p>
            {uploading && (
              <div className={styles.uploadingIndicator}>
                <div className={styles.uploadingBar}></div>
                <span>Processing your files...</span>
              </div>
            )}
          </div>

          {/* Documents Grid */}
          {documents.length === 0 ? (
            <div className={styles.emptyState}>
              <FiBook size={48} />
              <h3>No documents uploaded</h3>
              <p>Upload your first document to get started</p>
            </div>
          ) : (
            <div className={styles.documentsGrid}>
              <div className={styles.documentsList}>
                <div className={styles.documentsListHeader}>
                  <h3 className={styles.sectionTitle}>Your Documents ({documents.length})</h3>
                  {(selectedDoc || selectedDocuments.length > 0) && (
                    <button
                      className={styles.chatWithDocsBtn}
                      onClick={handleStartChat}
                      disabled={chatLoading}
                      title="Chat with selected documents"
                    >
                      {chatLoading ? (
                        <FiLoader size={16} className={styles.spinner} />
                      ) : (
                        <FiMessageSquare size={16} />
                      )}
                      {!chatLoading && (selectedDocuments.length > 0 
                        ? `Chat with ${selectedDocuments.length} doc${selectedDocuments.length !== 1 ? 's' : ''}`
                        : 'Chat with document')}
                    </button>
                  )}
                </div>
                {documents.map((doc) => {
                  const isSelected = selectedDocuments.some(d => d.id === doc.id);
                  return (
                    <motion.div
                      key={doc.id}
                      className={`${styles.documentCard} ${selectedDoc?.id === doc.id ? styles.documentCardActive : ''} ${isSelected ? styles.documentCardSelected : ''}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <input
                        type="checkbox"
                        className={styles.docCheckbox}
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleDocumentSelection(doc);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div
                        className={styles.docClickable}
                        onClick={() => setSelectedDoc(doc)}
                      >
                        <div className={styles.docIcon}>
                          {getFileIcon(doc.type, doc.name)}
                        </div>
                        <div className={styles.docInfo}>
                          <div className={styles.docName}>{doc.name}</div>
                          <div className={styles.docMeta}>
                            {formatFileSize(doc.size)} • {new Date(doc.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      <button
                        className={styles.deleteBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(doc.id);
                        }}
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </motion.div>
                  );
                })}
              </div>

              {/* Actions Panel */}
              {selectedDoc && (
                <motion.div
                  className={styles.actionsPanel}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <h3 className={styles.sectionTitle}>Generate From Document</h3>
                  <p className={styles.actionHint}>
                    Selected: <strong>{selectedDoc.name}</strong>
                    {processedDocCache[selectedDoc.id] && (
                      <span className={styles.cachedBadge} title="This document has been processed and cached. Generate unlimited content without rate limits!">
                        💾 Cached
                      </span>
                    )}
                  </p>

                  {/* Page Range Selector */}
                  {showPageSelector && (
                    <div className={styles.pageSelector}>
                      <label className={styles.pageLabel}>
                        📖 Select Pages to Process:
                      </label>
                      <div className={styles.pageRangeInputs}>
                        <div className={styles.pageInputGroup}>
                          <label>From:</label>
                          <input
                            type="number"
                            min="1"
                            max={totalPages || 999}
                            value={pageRange.start}
                            onChange={(e) => {
                              const val = Math.max(1, Math.min(parseInt(e.target.value) || 1, totalPages || 999));
                              setPageRange({ ...pageRange, start: val });
                            }}
                            className={styles.pageInput}
                          />
                        </div>
                        <span className={styles.pageSeparator}>to</span>
                        <div className={styles.pageInputGroup}>
                          <label>To:</label>
                          <input
                            type="number"
                            min={pageRange.start}
                            max={totalPages || 999}
                            value={pageRange.end === null ? totalPages || '' : pageRange.end}
                            onChange={(e) => {
                              const val = Math.max(pageRange.start, Math.min(parseInt(e.target.value) || 1, totalPages || 999));
                              setPageRange({ ...pageRange, end: val });
                            }}
                            className={styles.pageInput}
                          />
                        </div>
                      </div>
                      <div className={styles.pageQuickSelect}>
                        <button onClick={() => setPageRange({ start: 1, end: 10 })} className={styles.quickBtn}>
                          First 10
                        </button>
                        <button onClick={() => setPageRange({ start: 1, end: 25 })} className={styles.quickBtn}>
                          First 25
                        </button>
                        <button onClick={() => setPageRange({ start: 1, end: 50 })} className={styles.quickBtn}>
                          First 50
                        </button>
                        {totalPages && (
                          <button 
                            onClick={() => setPageRange({ start: 1, end: totalPages })} 
                            className={styles.quickBtn}
                            style={{ fontWeight: pageRange.end === totalPages ? 700 : 400 }}
                          >
                            All ({totalPages})
                          </button>
                        )}
                      </div>
                      <p className={styles.pageHint}>
                        📄 Processing {(() => {
                          const endPage = pageRange.end === null ? totalPages : pageRange.end;
                          const pageCount = endPage - pageRange.start + 1;
                          return pageCount === totalPages 
                            ? `ALL ${totalPages} pages` 
                            : `${pageCount} pages`;
                        })()}
                        {totalPages && ` (Total: ${totalPages} pages)`}
                        <br />
                        <span style={{ 
                          fontSize: '0.85em', 
                          color: '#22c55e', 
                          fontWeight: 500 
                        }}>
                          {(() => {
                            const endPage = pageRange.end === null ? totalPages : pageRange.end;
                            const pageCount = endPage - pageRange.start + 1;
                            if (pageRange.end === totalPages || pageRange.end === null) {
                              return `✅ Processing complete document (${pageCount} pages)!`;
                            } else {
                              return `📄 Processing ${pageCount} pages (${pageRange.start}-${endPage})`;
                            }
                          })()}
                        </span>
                      </p>
                    </div>
                  )}

                  <div className={styles.actionButtons}>
                    <button
                      className={styles.actionBtn}
                      onClick={() => handleGenerate('quiz')}
                      disabled={processing}
                    >
                      {processing && processingType === 'quiz' ? (
                        <FiLoader size={20} className={styles.spinner} />
                      ) : (
                        <FiBook size={20} />
                      )}
                      <div>
                        <div className={styles.actionBtnTitle}>Quiz</div>
                        <div className={styles.actionBtnDesc}>Interactive quiz questions</div>
                      </div>
                    </button>

                    <button
                      className={styles.actionBtn}
                      onClick={() => handleGenerate('test')}
                      disabled={processing}
                    >
                      {processing && processingType === 'test' ? (
                        <FiLoader size={20} className={styles.spinner} />
                      ) : (
                        <FiClipboard size={20} />
                      )}
                      <div>
                        <div className={styles.actionBtnTitle}>Test</div>
                        <div className={styles.actionBtnDesc}>Comprehensive assessment</div>
                      </div>
                    </button>

                    <button
                      className={styles.actionBtn}
                      onClick={() => handleGenerate('flashcards')}
                      disabled={processing}
                    >
                      {processing && processingType === 'flashcards' ? (
                        <FiLoader size={20} className={styles.spinner} />
                      ) : (
                        <FiZap size={20} />
                      )}
                      <div>
                        <div className={styles.actionBtnTitle}>Flashcards</div>
                        <div className={styles.actionBtnDesc}>Study cards for review</div>
                      </div>
                    </button>

                    <button
                      className={styles.actionBtn}
                      onClick={() => handleGenerate('notes')}
                      disabled={processing}
                    >
                      {processing && processingType === 'notes' ? (
                        <FiLoader size={20} className={styles.spinner} />
                      ) : (
                        <FiLayers size={20} />
                      )}
                      <div>
                        <div className={styles.actionBtnTitle}>Study Notes</div>
                        <div className={styles.actionBtnDesc}>Organized summaries</div>
                      </div>
                    </button>

                    <button
                      className={styles.actionBtn}
                      onClick={() => handleGenerate('quotes')}
                      disabled={processing}
                    >
                      {processing && processingType === 'quotes' ? (
                        <FiLoader size={20} className={styles.spinner} />
                      ) : (
                        <FiMessageSquare size={20} />
                      )}
                      <div>
                        <div className={styles.actionBtnTitle}>Key Quotes</div>
                        <div className={styles.actionBtnDesc}>Meaningful passages + analysis</div>
                      </div>
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          )}
        </div>
      </motion.div>

      {/* Document Chat Modal */}
      <AnimatePresence>
        {showDocumentChat && Array.isArray(showDocumentChat) && (
          <DocumentChatV2
            documents={showDocumentChat}
            allDocuments={documents}
            initialPageNumber={initialPageNumber}
            onClose={() => {
              setShowDocumentChat(false);
              setInitialPageNumber(null); // Reset page number
            }}
            onMinimize={() => setIsChatMinimized(true)}
            onOpenDocument={(doc) => {
              console.log('📄 Document clicked in chat:', doc.name, doc.id);
              // Find the actual document from documents array by id
              const actualDoc = documents.find(d => d.id === doc.id);
              console.log('📋 Found document in library:', actualDoc?.name);
              if (actualDoc) {
                setSelectedDoc(actualDoc);
                console.log('✅ Selected doc set to:', actualDoc.name);
              } else {
                console.warn('⚠️ Document not found in library. Looking by name...');
                // Fallback: try to find by name if ID doesn't match
                const docByName = documents.find(d => d.name === doc.name);
                if (docByName) {
                  setSelectedDoc(docByName);
                  console.log('✅ Found by name and selected:', docByName.name);
                }
              }
            }}
            isMinimized={isChatMinimized}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default DocumentLibrary;
